import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download } from 'lucide-react';
import axios from 'axios';
import { useFormProtection } from '../hooks/useFormProtection';
import HoneypotField from './HoneypotField';
import RecaptchaNotice from './RecaptchaNotice';

const ExecutiveDashboardPanelCTA = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    firm_name: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const navigate = useNavigate();
  const { honeypotProps, onInteraction, getProtectionPayload } = useFormProtection('pdf_download');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const protection = await getProtectionPayload();

      // The notification endpoint is the anti-abuse gate. If it rejects the
      // submission we must not hand out the PDF, so this error is no longer
      // swallowed.
      await axios.post('/api/send-pdf-notification', { ...formData, ...protection });
      console.log('Email notifications sent');

      // Create form and submit to trigger download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/download-pdf';
      form.target = '_blank';
      form.style.display = 'none';

      // Add form fields (including the protection payload)
      const downloadFields = { ...formData, ...protection };
      Object.keys(downloadFields).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = downloadFields[key] ?? '';
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      setSubmitMessage('Thanks! Your PDF is downloading now.');
      
      // Reset form after successful download
      setTimeout(() => {
        setFormData({
          full_name: '',
          email: '',
          firm_name: ''
        });
        setSubmitMessage('');
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'An error occurred. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <div className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-2xl p-6 sm:p-8 border border-gray-200">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-orange-500 mr-2" />
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                See Your Data in Action
              </h3>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Download our Executive Dashboard sample to see what your Practice CS data could look like
            </p>
          </div>

          <form onSubmit={handleSubmit} onFocus={onInteraction} className="max-w-2xl mx-auto">
            <HoneypotField {...honeypotProps} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                placeholder="Full Name"
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors min-h-[50px]"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Email Address"
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors min-h-[50px]"
              />
              <input
                type="text"
                name="firm_name"
                value={formData.firm_name}
                onChange={handleInputChange}
                required
                placeholder="Firm Name"
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors min-h-[50px]"
              />
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 min-h-[48px] flex items-center justify-center mx-auto"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Get My Dashboard Sample
                  </>
                )}
              </button>

              {submitMessage && (
                <div className={`mt-4 p-3 rounded-lg text-center max-w-md mx-auto ${
                  submitMessage.includes('Thanks!') 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}
            </div>
          </form>

          <p className="text-xs text-gray-500 text-center mt-4">
            Free download • No spam • See what your dashboards could look like
          </p>

          <RecaptchaNotice className="mt-2" />
        </div>
      </div>
    </section>
  );
};

export default ExecutiveDashboardPanelCTA;